import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, ArrowLeft, Search } from 'lucide-react';
import { getAllPosts, getFeaturedPosts } from '../../content/blog/posts';
import { generateSEOMetadata } from '../../utils/blogUtils';
import BlogCard from './BlogCard';
import BlogNavigation from './BlogNavigation';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);

  useEffect(() => {
    const allPosts = getAllPosts();
    const featured = getFeaturedPosts();
    
    setPosts(allPosts);
    setFeaturedPosts(featured);
    setFilteredPosts(allPosts);

    // Set page title and meta description
    document.title = 'Blog - PDF Statement Converter | SA Accounting Tips & Guides';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert guides on South African bank statement conversion, Sage import tips, and accounting software tutorials. Free resources for SA businesses.');
    }
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPosts(filtered);
    }
  }, [searchTerm, posts]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PDF STATEMENT CONVERTER</span>
            </div>
            
            <div className="hidden md:block">
              <Link
                to="/login"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                Try Converter
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Blog Navigation */}
      <BlogNavigation />

      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Accounting Insights & Guides
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Expert tips for South African businesses on bank statement conversion, 
            Sage import processes, and accounting software optimization.
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Featured Posts */}
        {!searchTerm && featuredPosts.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.map((post) => (
                <BlogCard key={post.id} post={post} featured={true} />
              ))}
            </div>
          </div>
        )}

        {/* All Posts / Search Results */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {searchTerm ? `Search Results (${filteredPosts.length})` : 'All Articles'}
            </h2>
            
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
              >
                Clear search
              </button>
            )}
          </div>

          {filteredPosts.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No articles found' : 'No articles available'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm 
                  ? `No articles match "${searchTerm}". Try different keywords.`
                  : 'Check back later for new content.'
                }
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-indigo-600 hover:text-indigo-500 font-medium"
                >
                  View all articles
                </button>
              )}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-indigo-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Convert Your Bank Statements?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Put these tips into practice! Convert your SA bank statements to Sage-ready CSV format in seconds.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Try PDF Statement Converter
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">PDF Statement Converter</span>
          </div>
          <p className="text-gray-600 mb-4">Convert South African bank statements to Sage-ready CSV format</p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-gray-700">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-gray-700">
              Terms of Service
            </Link>
            <Link to="/" className="hover:text-gray-700">
              Back to Converter
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;